/** A room's way into its conversations, on the room's page: the card, the phone row, and the circle made from a group. */
import { useNavigate } from "react-router";
import type { HostCircleRow, HostRoomRow } from "../../../shared/talk";
import type { RoomGroupKey, RoomScope } from "../../../shared/types";
import { Icon } from "../../components/ui/Icon";
import { useStartCircleIn } from "../../talk/handoff";
import { announcementsPath, circlePath, everyonePath, filesPath, questionsPath, roomHome } from "../../talk/places";
import { whenWords } from "../../talk/rooms";

/** The Conversations card: each place opens in Conversations, and Start a circle opens its sheet for this room. */
export function RoomTalkCard({ row }: { row: HostRoomRow }) {
  const navigate = useNavigate();
  const startCircle = useStartCircleIn();
  const home = roomHome(row);
  return (
    <section className="card rp-talk">
      <div className="rp-h">
        <span className="eyebrow">Conversations</span>
        <button type="button" className="rp-open" onClick={() => navigate(home)}>
          Open <Icon name="right" />
        </button>
      </div>
      {row.questionsWaiting !== null ? (
        <button type="button" className="rp-row" onClick={() => navigate(questionsPath(row.webinarId))}>
          <Icon name="chat" />
          <span className="rp-n">Questions</span>
          {row.questionsWaiting ? <i className="ch-new">{row.questionsWaiting}</i> : null}
        </button>
      ) : null}
      {row.everyoneChat ? (
        <button type="button" className="rp-row" onClick={() => navigate(everyonePath(row.webinarId))}>
          <Icon name="users" />
          <span className="rp-n">Everyone</span>
          {row.everyoneNew ? <i className="ch-new">{row.everyoneNew}</i> : null}
        </button>
      ) : null}
      <button type="button" className="rp-row" onClick={() => navigate(announcementsPath(row.webinarId))}>
        <Icon name="bell" />
        <span className="rp-n">Announcements</span>
        {row.lastAnnouncementAt !== null ? <small>{whenWords(row.lastAnnouncementAt)}</small> : null}
      </button>
      <button type="button" className="rp-row" onClick={() => navigate(filesPath(row.webinarId))}>
        <Icon name="fold" />
        <span className="rp-n">Files</span>
        <small>{row.filesCount}</small>
      </button>
      <p className="rp-sub">Circles</p>
      {row.circles.map((c) => (
        <button key={c.circleId} type="button" className="rp-row" onClick={() => navigate(circlePath(c.circleId))}>
          <Icon name="target" />
          <span className="rp-n">{c.name}</span>
          {c.live ? <span className="ch-live">Live</span> : c.newCount ? <i className="ch-new">{c.newCount}</i> : <small>{c.members}</small>}
        </button>
      ))}
      <button type="button" className="rp-row add" onClick={() => startCircle(row.webinarId, home)}>
        <Icon name="plus" />
        <span className="rp-n">Start a circle</span>
      </button>
    </section>
  );
}

/** The line under "Conversations" on a phone: what is new, and how many circles. */
export function talkRowLine(row: HostRoomRow): string {
  const parts: string[] = [];
  if (row.questionsWaiting) parts.push(`${row.questionsWaiting} ${row.questionsWaiting === 1 ? "question" : "questions"} waiting`);
  if (row.everyoneChat && row.everyoneNew) parts.push(`${row.everyoneNew} new in Everyone`);
  if (row.circles.length) parts.push(`${row.circles.length} ${row.circles.length === 1 ? "circle" : "circles"}`);
  return parts.length ? parts.join(" · ") : row.everyoneChat ? "Everyone, announcements and files" : "Announcements and files";
}

/** How a circle stores the group it was made from: "session:end:<live>" for one live's group, "all:watched_end" for the rest. */
export function madeFromOf(scope: RoomScope, key: RoomGroupKey, sessionId?: string): string {
  return `${scope}:${key}${scope === "session" && sessionId ? `:${sessionId}` : ""}`;
}

/** The circle made from this group, if there is one. */
export function circleMadeFrom(circles: readonly HostCircleRow[] | undefined, scope: RoomScope, key: RoomGroupKey, sessionId?: string): HostCircleRow | null {
  const made = madeFromOf(scope, key, sessionId);
  return circles?.find((c) => c.madeFrom === made) ?? null;
}
